/**
 * Runtime for the ICU MessageFormat subset the shared catalog allows (#799, docs/I18N.md "Message syntax").
 * A port of shared/i18n/tools/icu.py's parser, plus a formatter. The validator (gen.py) already rejects anything
 * outside the subset, so this parser is lenient: it never throws, and junk renders as literal text.
 *
 *   text  {name}  {n, number}  {count, plural, one {# order} other {# orders}}  {side, select, yes {…} other {…}}
 *   '' = a literal apostrophe; '{' '}' '#' quote syntax characters.
 *
 * Arguments may be strings, numbers or (for rich messages) React nodes; format() returns the pieces so callers can
 * join them into a string or render them as children.
 */

type Node =
  | string
  | { k: 'arg'; name: string; num: boolean }
  | { k: 'pound' }
  | { k: 'plural' | 'select'; name: string; options: Record<string, Node[]> };

export type Message = Node[];

const NAME = /[A-Za-z0-9_]/;

class Parser {
  i = 0;
  constructor(private readonly s: string) {}

  private ws() {
    while (this.i < this.s.length && /\s/.test(this.s[this.i]!)) this.i++;
  }

  private word(): string {
    this.ws();
    const start = this.i;
    while (this.i < this.s.length && /[A-Za-z0-9_=-]/.test(this.s[this.i]!)) this.i++;
    return this.s.slice(start, this.i);
  }

  message(inPlural: boolean, depth: number): Node[] {
    const out: Node[] = [];
    let buf = '';
    const flush = () => {
      if (buf) out.push(buf);
      buf = '';
    };
    while (this.i < this.s.length) {
      const c = this.s[this.i]!;
      if (c === "'") {
        const nxt = this.s[this.i + 1] ?? '';
        if (nxt === "'") {
          buf += "'";
          this.i += 2;
        } else if (nxt === '{' || nxt === '}' || (inPlural && nxt === '#')) {
          this.i++;
          while (this.i < this.s.length) {
            if (this.s[this.i] === "'") {
              if (this.s[this.i + 1] === "'") {
                buf += "'";
                this.i += 2;
                continue;
              }
              this.i++;
              break;
            }
            buf += this.s[this.i];
            this.i++;
          }
        } else {
          buf += "'";
          this.i++;
        }
      } else if (c === '{') {
        flush();
        out.push(this.argument(depth));
      } else if (c === '}') {
        if (depth === 0) {
          buf += c;
          this.i++;
        } else break;
      } else if (c === '#' && inPlural) {
        flush();
        out.push({ k: 'pound' });
        this.i++;
      } else {
        buf += c;
        this.i++;
      }
    }
    flush();
    return out;
  }

  private argument(depth: number): Node {
    this.i++; // '{'
    this.ws();
    const start = this.i;
    while (this.i < this.s.length && NAME.test(this.s[this.i]!)) this.i++;
    const name = this.s.slice(start, this.i);
    this.ws();
    if (this.s[this.i] === '}') {
      this.i++;
      return { k: 'arg', name, num: false };
    }
    if (this.s[this.i] === ',') this.i++;
    const kind = this.word();
    this.ws();
    if (kind === 'plural' || kind === 'select') {
      if (this.s[this.i] === ',') this.i++;
      const options: Record<string, Node[]> = {};
      for (;;) {
        this.ws();
        if (this.i >= this.s.length) break;
        if (this.s[this.i] === '}') {
          this.i++;
          break;
        }
        const sel = this.word();
        this.ws();
        if (this.s[this.i] !== '{' || !sel) break;
        this.i++;
        options[sel] = this.message(kind === 'plural', depth + 1);
        if (this.s[this.i] === '}') this.i++;
      }
      return { k: kind, name, options };
    }
    // {n, number} / {n, number, integer}
    while (this.i < this.s.length && this.s[this.i] !== '}') this.i++;
    this.i++;
    return { k: 'arg', name, num: true };
  }
}

export function parse(src: string): Message {
  return new Parser(src).message(false, 0);
}

export type ArgValue = string | number | boolean | null | undefined | object;

export interface FormatEnv {
  /** CLDR plural category for a count in the active language. */
  plural: (n: number) => string;
  /** Integer formatting for {n, number} and `#` (locale grouping). */
  number: (n: number) => string;
  /**
   * Right-to-left locale (ar, he): wrap every interpolated value (string/number arguments, `#`, rich nodes) in
   * FSI … PDI, so a Latin title, a price or an id keeps its own direction and does not reorder the sentence
   * around it (#900). Off for left-to-right locales, whose output is unchanged.
   */
  isolate?: boolean;
}

/** U+2068 FIRST STRONG ISOLATE and U+2069 POP DIRECTIONAL ISOLATE (Unicode bidi isolates). */
export const FSI = '\u2068';
export const PDI = '\u2069';

/** Message + arguments → pieces (strings and whatever non-string values were passed for string arguments). */
export function format(msg: Message, args: Record<string, ArgValue> | undefined, env: FormatEnv): unknown[] {
  const out: unknown[] = [];
  const value = (v: unknown) => {
    if (env.isolate) out.push(FSI, v, PDI);
    else out.push(v);
  };
  const walk = (nodes: Node[], count: number | undefined) => {
    for (const n of nodes) {
      if (typeof n === 'string') out.push(n);
      else if (n.k === 'pound') {
        if (count === undefined) out.push('#');
        else value(env.number(count));
      } else if (n.k === 'arg') {
        const v = args?.[n.name];
        if (n.num && typeof v === 'number') value(env.number(v));
        else if (v === undefined || v === null) out.push(`{${n.name}}`);
        else value(typeof v === 'object' ? v : String(v));
      } else if (n.k === 'plural') {
        const v = Number(args?.[n.name]);
        const cat = Number.isFinite(v) ? env.plural(v) : 'other';
        walk(n.options[cat] ?? n.options.other ?? [], Number.isFinite(v) ? v : undefined);
      } else {
        const v = String(args?.[n.name] ?? 'other');
        walk(n.options[v] ?? n.options.other ?? [], count);
      }
    }
  };
  walk(msg, undefined);
  return out;
}
